import React, { useState, useEffect } from 'react';
import {
  FileText,
  Upload,
  Clock,
  AlertTriangle,
  CheckCircle,
  Trash2,
  Paperclip,
  Loader
} from 'lucide-react';

interface PublicSpecialRequestViewProps {
  token: string;
}

import { parseRawOptions } from './shared/FormBuilder';
import { FormBuilderField } from '../types';

type SelectedFile = { filename: string; mimeType: string; base64Data: string; size: number; docName?: string; fieldId?: string };

export default function PublicSpecialRequestView({ token }: PublicSpecialRequestViewProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expired, setExpired] = useState(false);
  const [expiredReason, setExpiredReason] = useState('');

  const [request, setRequest] = useState<any>(null);
  const [applicant, setApplicant] = useState<any>(null);
  const [workspace, setWorkspace] = useState<any>(null);
  // Organization requests only: set when the endpoint reports a
  // submitted request that is pending review/approval.
  const [pendingApproval, setPendingApproval] = useState<any>(null);

  // Form States
  const [responseText, setResponseText] = useState('');
  const [selectedChoices, setSelectedChoices] = useState<string[]>([]);
  const [formResponses, setFormResponses] = useState<Record<string, any>>({});
  const [selectedFiles, setSelectedFiles] = useState<SelectedFile[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  // ADM-01 close-out (Batch R2): file and submission validation render
  // in an in-page banner (the PublicSigningView APP-04 pattern), never
  // a native alert.
  const [formError, setFormError] = useState<string | null>(null);

  // Fetch Special Request Details
  useEffect(() => {
    async function fetchDetails() {
      try {
        setLoading(true);
        const res = await fetch(`/api/public/special-requests/${token}`);
        if (!res.ok) {
          const errData = await res.json().catch(() => ({}));
          throw new Error(errData.error || 'Failed to load request details.');
        }
        const data = await res.json();
        if (data.pendingApproval) {
          setPendingApproval(data);
          setRequest(data.request);
          setApplicant(data.applicant);
        } else if (data.expired) {
          setExpired(true);
          setExpiredReason(data.reason || 'This link is no longer available.');
        } else {
          setRequest(data.request);
          setApplicant(data.applicant);
          setWorkspace(data.workspace);
        }
      } catch (err: any) {
        console.error('Error fetching special request details:', err);
        setError(err.message || 'An unexpected error occurred while loading the request.');
      } finally {
        setLoading(false);
      }
    }
    if (token) {
      fetchDetails();
    }
  }, [token]);

  // Handle File Input
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, docName?: string, fieldId?: string) => {
    const files = e.target.files;
    if (!files) return;

    const allowedMimes = new Set([
      'application/pdf', 'image/jpeg', 'image/png', 'image/gif',
      'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'text/plain',
    ]);

    Array.from(files).forEach(file => {
      if (file.size > 10 * 1024 * 1024) {
        setFormError(`File "${file.name}" exceeds the 10MB limit.`);
        return;
      }

      if (!allowedMimes.has(file.type)) {
        setFormError(`File "${file.name}" has an unsupported format. Allowed: PDF, JPEG, PNG, GIF, Word, TXT.`);
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        const base64Data = (reader.result as string).split(',')[1];
        setSelectedFiles(prev => {
          let filtered = prev;
          if (docName) {
            filtered = prev.filter(f => f.docName !== docName);
          }
          if (fieldId) {
            filtered = filtered.filter(f => f.fieldId !== fieldId);
          }
          return [
            ...filtered,
            {
              filename: file.name,
              mimeType: file.type,
              base64Data,
              size: file.size,
              docName: docName || undefined,
              fieldId: fieldId || undefined
            }
          ];
        });
      };
      reader.onerror = () => {
        setFormError(`Failed to read file: ${file.name}`);
      };
      reader.readAsDataURL(file);
    });

    // Reset input
    e.target.value = '';
  };

  // Remove Selected File
  const handleRemoveFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Remove Selected File by Doc Name
  const handleRemoveFileByDocName = (docName: string) => {
    setSelectedFiles(prev => prev.filter(f => f.docName !== docName));
  };

  const handleRemoveFileByFieldId = (fieldId: string) => {
    setSelectedFiles(prev => prev.filter(f => f.fieldId !== fieldId));
  };

  const getFormFields = (): FormBuilderField[] => Array.isArray(request?.formFields) ? request.formFields : [];

  const setFieldResponse = (fieldId: string, value: any) => {
    setFormResponses(prev => ({ ...prev, [fieldId]: value }));
  };

  // Form Submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    if (!token) return;

    // Validate
    if (request.responseType === 'short_text' || request.responseType === 'long_text' || request.responseType === 'date' || request.responseType === 'dropdown') {
      if (!responseText.trim()) {
        setFormError('Please provide your response before submitting.');
        return;
      }
    } else if (request.responseType === 'multiple_choice') {
      if (selectedChoices.length === 0) {
        setFormError('Please select at least one option before submitting.');
        return;
      }
    } else if (request.responseType === 'file_upload') {
      if (request.requiredDocs && Array.isArray(request.requiredDocs) && request.requiredDocs.length > 0) {
        for (const docName of request.requiredDocs) {
          const hasFile = selectedFiles.some(f => f.docName === docName);
          if (!hasFile) {
            setFormError(`Please upload a file for "${docName}" before submitting.`);
            return;
          }
        }
      } else {
        if (selectedFiles.length === 0) {
          setFormError('Please select/upload at least one file before submitting.');
          return;
        }
      }
    } else if (request.responseType === 'form') {
      const fields = getFormFields();
      if (fields.length > 0) {
        for (const field of fields) {
          if (!field.required) continue;
          if (field.type === 'file_upload') {
            if (!selectedFiles.some(f => f.fieldId === field.id)) {
              setFormError(`Please upload a file for "${field.label}" before submitting.`);
              return;
            }
          } else {
            const value = formResponses[field.id];
            if (Array.isArray(value) ? value.length === 0 : !String(value || '').trim()) {
              setFormError(`Please answer "${field.label}" before submitting.`);
              return;
            }
          }
        }
      } else if (!responseText.trim()) {
        setFormError('Please provide your response text before submitting.');
        return;
      }
      // If specific requiredDocs file slots are specified, validate that each slot has an uploaded file
      if (request.requiredDocs && Array.isArray(request.requiredDocs) && request.requiredDocs.length > 0) {
        for (const docName of request.requiredDocs) {
          const hasFile = selectedFiles.some(f => f.docName === docName);
          if (!hasFile) {
            setFormError(`Please upload a file for "${docName}" before submitting.`);
            return;
          }
        }
      }
    }

    try {
      setSubmitting(true);
      const res = await fetch(`/api/public/special-requests/${token}/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          responseText: (request.responseType === 'short_text' || request.responseType === 'long_text' || request.responseType === 'date' || request.responseType === 'dropdown') ? responseText : (request.responseType === 'form' && getFormFields().length === 0 ? responseText : undefined),
          formResponses: request.responseType === 'form' && getFormFields().length > 0 ? formResponses : undefined,
          selectedValues: request.responseType === 'multiple_choice' ? selectedChoices : undefined,
          files: (request.responseType === 'file_upload' || request.responseType === 'form') ? selectedFiles : undefined
        })
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || 'Failed to submit your response.');
      }

      setSuccess(true);
    } catch (err: any) {
      console.error('Error submitting response:', err);
      setFormError(err.message || 'An unexpected error occurred during submission.');
    } finally {
      setSubmitting(false);
    }
  };

  // Render Loading State
  if (loading) {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-slate-50 p-6">
        <div className="text-center space-y-4">
          <Loader className="h-10 w-10 text-brand animate-spin mx-auto" />
          <p className="text-sm font-semibold text-slate-600">Loading your Special Request...</p>
        </div>
      </div>
    );
  }

  // Render Error State
  if (error) {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-slate-50 p-6">
        <div className="max-w-md w-full bg-white border border-slate-200 rounded-3xl p-8 shadow-xl text-center space-y-4">
          <div className="h-12 w-12 rounded-full bg-red-100 flex items-center justify-center mx-auto text-red-600">
            <AlertTriangle className="h-6 w-6" />
          </div>
          <h2 className="text-lg font-bold text-slate-800">Error Loading Request</h2>
          <p className="text-sm text-slate-500 leading-relaxed">{error}</p>
        </div>
      </div>
    );
  }

  // Render Expired/Completed State
  if (expired) {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-slate-50 p-6">
        <div className="max-w-md w-full bg-white border border-slate-200 rounded-3xl p-8 shadow-xl text-center space-y-5">
          <div className="h-14 w-12 rounded-full bg-amber-50 flex items-center justify-center mx-auto text-amber-500">
            <Clock className="h-7 w-7 animate-pulse" />
          </div>
          <h2 className="text-xl font-extrabold text-slate-900">Request No Longer Available</h2>
          <p className="text-sm text-slate-500 leading-relaxed">{expiredReason}</p>
          <div className="pt-2 text-[11px] text-slate-400">
            If you believe this is in error, please contact your placement program advisor directly.
          </div>
        </div>
      </div>
    );
  }

  // Render Pending Review/Approval State (organization requests that
  // were submitted and require approval — no form, no editable fields)
  if (pendingApproval) {
    const completedAt = pendingApproval.request?.completedAt;
    return (
      <div className="min-h-dvh flex items-center justify-center bg-slate-50 p-6">
        <div className="max-w-md w-full bg-white border border-slate-200 rounded-3xl p-8 shadow-xl text-center space-y-5">
          <div className="h-14 w-14 rounded-full bg-amber-100 flex items-center justify-center mx-auto text-amber-600">
            <Clock className="h-8 w-8" />
          </div>
          <h2 className="text-2xl font-extrabold text-slate-900">Pending Review / Approval</h2>
          <p className="text-sm text-slate-500 leading-relaxed">
            Your response to the request <strong>"{pendingApproval.request?.title}"</strong> was
            completed{completedAt ? <> on <strong>{new Date(completedAt).toLocaleString()}</strong></> : ''} and
            is now pending review and approval by the administration.
          </p>
          <p className="text-xs text-slate-400">
            No further action is needed right now. The status will update once the review is
            complete. You can now close this tab.
          </p>
        </div>
      </div>
    );
  }

  // Render Success State
  if (success) {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-slate-50 p-6">
        <div className="max-w-md w-full bg-white border border-slate-200 rounded-3xl p-8 shadow-xl text-center space-y-5">
          <div className="h-14 w-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto text-emerald-600">
            <CheckCircle className="h-8 w-8" />
          </div>
          <h2 className="text-2xl font-extrabold text-slate-900">Response Submitted!</h2>
          <p className="text-sm text-slate-500 leading-relaxed">
            Thank you, <strong>{applicant?.name}</strong>. Your response to the request <strong>"{request?.title}"</strong> has been securely submitted and your advisor has been notified.
          </p>
          <p className="text-xs text-slate-400">
            You can now close this tab.
          </p>
        </div>
      </div>
    );
  }

  // Format File Size
  const formatSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const branding = workspace?.branding || {};
  const primaryColor = branding.primary || '#2563eb'; // blue-600 default
  const primaryDarkColor = branding.primaryDark || '#1d4ed8'; // blue-700 default
  const accentColor = branding.accent || primaryColor; // fallback to primary

  // Verification requests: no form — completion is confirmed by the
  // identity provider (Didit). This page only shows the live status
  // and, while the session is active, the link to continue it.
  if (request?.requestType === 'Verification') {
    const v = request.verification || {};
    const vStatus = String(v.status || 'not_started').replace(/_/g, ' ');
    return (
      <div className="min-h-dvh flex items-center justify-center bg-slate-50 p-6">
        <div className="max-w-md w-full bg-white border border-slate-200 rounded-3xl p-8 shadow-xl text-center space-y-5">
          <div className="h-14 w-14 rounded-full bg-violet-100 flex items-center justify-center mx-auto text-violet-600">
            <FileText className="h-7 w-7" />
          </div>
          <h2 className="text-xl font-extrabold text-slate-900">{request.title}</h2>
          <p className="text-sm text-slate-500 leading-relaxed">{request.description}</p>
          {request.instructions && (
            <p className="text-sm text-slate-500 leading-relaxed">{request.instructions}</p>
          )}
          <div className="bg-slate-50 border border-slate-200 rounded-xl px-4 py-3">
            <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Verification Status</p>
            <p className="text-sm font-bold text-slate-700 capitalize">{vStatus}</p>
            {v.issue && <p className="text-xs text-red-500 mt-1">{v.issue}</p>}
          </div>
          {v.sessionUrl ? (
            <a
              href={v.sessionUrl}
              className="inline-block px-5 py-2.5 rounded-xl text-white text-sm font-bold"
              style={{ backgroundColor: primaryColor }}
            >
              Continue Verification
            </a>
          ) : (
            <p className="text-xs text-slate-400">
              If you have not received your verification link by email, please contact the administrator who issued this request.
            </p>
          )}
          <p className="text-[11px] text-slate-400">
            This request completes automatically once your identity verification is confirmed. No form submission is required.
          </p>
        </div>
      </div>
    );
  }

  // Main UI Form
  return (
    <div className="min-h-dvh bg-slate-50 flex flex-col">
      {/* Upper header with branding */}
      <header className="w-full bg-white border-b border-slate-200 py-4 px-6 flex items-center justify-between shrink-0 shadow-sm">
        <div className="flex items-center space-x-3">
          {branding.logoUrl || workspace?.logoUrl ? (
            <img src={branding.logoUrl || workspace.logoUrl} alt={workspace?.name || 'Workspace Logo'} className="h-8 w-auto object-contain rounded" />
          ) : (
            <div className="h-8 w-8 rounded-lg flex items-center justify-center text-white font-extrabold text-sm shadow-sm" style={{ backgroundColor: primaryColor }}>
              {workspace?.name?.charAt(0) || 'W'}
            </div>
          )}
          <span className="font-extrabold text-slate-800 text-sm tracking-tight">{workspace?.name || 'Placement Portal'}</span>
        </div>
        <div className="text-caption font-bold text-slate-400">Special Request Center</div>
      </header>

      {/* Main Container */}
      <main className="flex-1 max-w-lg w-full mx-auto px-4 py-8 flex flex-col justify-center">
        <div className="bg-white border border-slate-200 shadow-xl rounded-3xl overflow-hidden flex flex-col">
          {/* Header Banner */}
          <div className="px-6 py-6 text-white space-y-1 shrink-0" style={{ backgroundColor: primaryColor }}>
            <div className="flex items-center justify-between">
              <span className={`text-[10px] font-extrabold uppercase px-2.5 py-0.5 rounded-full border border-white/30 tracking-wider ${
                request?.priority === 'High' ? 'bg-red-500 text-white border-red-400' : 'bg-white/10 text-white'
              }`}>
                {request?.priority} Priority
              </span>
              <div className="flex items-center space-x-1 text-xs font-semibold text-blue-100">
                <Clock className="h-3.5 w-3.5" />
                <span>Due {new Date(request?.dueDate).toLocaleString()}</span>
              </div>
            </div>
            <h1 className="text-xl font-extrabold leading-tight pt-2">{request?.title}</h1>
            <p className="text-xs text-blue-100 font-medium">For candidate: {applicant?.name}</p>
          </div>

          {/* Form Content */}
          <form onSubmit={handleSubmit} className="p-6 space-y-6 flex-1 flex flex-col justify-between">
            {formError && (
              <div
                role="alert"
                className="bg-rose-50 border border-rose-300 text-rose-800 rounded-xl px-4 py-2.5 text-xs font-semibold"
              >
                {formError}
              </div>
            )}
            <div className="space-y-5">
              {/* Description Box */}
              <div className="space-y-1.5">
                <p className="text-xs font-extrabold text-slate-400 uppercase tracking-wider">Instructions</p>
                <div className="text-sm text-slate-700 leading-relaxed bg-slate-50 p-4 rounded-2xl border border-slate-200/60 whitespace-pre-wrap">
                  {request?.description}
                </div>
              </div>

              {/* Extra Instructions */}
              {request?.instructions && (
                <div className="space-y-1.5">
                  <p className="text-xs font-extrabold text-slate-400 uppercase tracking-wider">Additional Notes</p>
                  <p className="text-xs text-slate-500 italic bg-amber-50/50 p-3 rounded-xl border border-amber-100 leading-relaxed">
                    {request?.instructions}
                  </p>
                </div>
              )}

              {/* Input Area based on Response Type */}
              <div className="pt-2 border-t border-slate-100 space-y-2">
                <p className="text-xs font-extrabold text-slate-800 uppercase tracking-wider flex items-center space-x-1.5">
                  <span>Your Response</span>
                  <span className="text-red-500" aria-hidden="true">*</span><span className="sr-only"> (required)</span>
                </p>

                {request?.responseType === 'short_text' && (
                  <input
                    type="text"
                    required
                    value={responseText}
                    onChange={e => setResponseText(e.target.value)}
                    placeholder="Enter your response here..."
                    className="w-full px-4 py-3 text-sm border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-brand-primary/20 rounded-xl bg-white shadow-inner"
                  />
                )}

                {request?.responseType === 'long_text' && (
                  <textarea
                    required
                    rows={6}
                    value={responseText}
                    onChange={e => setResponseText(e.target.value)}
                    placeholder="Provide your detailed response here..."
                    className="w-full px-4 py-3 text-sm border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-brand-primary/20 rounded-2xl bg-white shadow-inner leading-relaxed"
                  />
                )}

                {request?.responseType === 'date' && (
                  <input
                    type="date"
                    required
                    value={responseText}
                    onChange={e => setResponseText(e.target.value)}
                    className="w-full px-4 py-3 text-sm border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-brand-primary/20 rounded-xl bg-white shadow-inner"
                  />
                )}

                {request?.responseType === 'dropdown' && (
                  <select
                    required
                    value={responseText}
                    onChange={e => setResponseText(e.target.value)}
                    className="w-full px-4 py-3 text-sm border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-brand-primary/20 rounded-xl bg-white shadow-inner font-semibold text-slate-700 cursor-pointer"
                  >
                    <option value="">-- Select an option --</option>
                    {(request?.dropdownOptions || []).map((opt: string, oIdx: number) => (
                      <option key={oIdx} value={opt}>{opt}</option>
                    ))}
                  </select>
                )}

                {request?.responseType === 'multiple_choice' && (
                  <div className="space-y-1.5">
                    <p className="text-xs text-slate-500">Select all that apply.</p>
                    {(request?.dropdownOptions || []).map((opt: string, oIdx: number) => (
                      <label key={oIdx} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedChoices.includes(opt)}
                          onChange={e => setSelectedChoices(prev =>
                            e.target.checked ? [...prev, opt] : prev.filter(v => v !== opt))}
                          className="w-4 h-4 text-brand rounded border-slate-300"
                        />
                        {opt}
                      </label>
                    ))}
                  </div>
                )}

                {request?.responseType === 'form' && getFormFields().length > 0 && (
                  <div className="space-y-4">
                    {getFormFields().map((field) => {
                      const value = formResponses[field.id] || (field.type === 'multi_select' || field.type === 'checkbox' ? [] : '');
                      const fieldFile = selectedFiles.find(f => f.fieldId === field.id);
                      const opts = parseRawOptions(field.options);

                      return (
                        <div key={field.id} className="space-y-2 p-4 bg-slate-50/70 rounded-2xl border border-slate-200">
                          <label className="text-xs font-bold text-slate-800 block">
                            {field.label} {field.required !== false && <span className="text-rose-500 ml-1">*</span>}
                          </label>
                          {field.helpText && <p className="text-xs text-slate-500">{field.helpText}</p>}

                          {field.type === 'short_text' && (
                            <input
                              type="text"
                              required={field.required !== false}
                              value={value}
                              onChange={e => setFieldResponse(field.id, e.target.value)}
                              placeholder={field.placeholder || 'Enter response...'}
                              className="w-full px-4 py-3 text-sm border border-slate-200 rounded-xl bg-white outline-none focus:border-brand"
                            />
                          )}

                          {field.type === 'long_text' && (
                            <textarea
                              required={field.required !== false}
                              rows={4}
                              value={value}
                              onChange={e => setFieldResponse(field.id, e.target.value)}
                              placeholder={field.placeholder || 'Provide detailed answer...'}
                              className="w-full px-4 py-3 text-sm border border-slate-200 rounded-2xl bg-white outline-none focus:border-brand"
                            />
                          )}

                          {field.type === 'number' && (
                            <input
                              type="number"
                              required={field.required !== false}
                              value={value}
                              onChange={e => setFieldResponse(field.id, e.target.value)}
                              placeholder={field.placeholder || '0'}
                              className="w-full px-4 py-3 text-sm border border-slate-200 rounded-xl bg-white outline-none focus:border-brand"
                            />
                          )}

                          {field.type === 'email' && (
                            <input
                              type="email"
                              required={field.required !== false}
                              value={value}
                              onChange={e => setFieldResponse(field.id, e.target.value)}
                              placeholder={field.placeholder || 'name@example.com'}
                              className="w-full px-4 py-3 text-sm border border-slate-200 rounded-xl bg-white outline-none focus:border-brand"
                            />
                          )}

                          {field.type === 'phone' && (
                            <input
                              type="tel"
                              required={field.required !== false}
                              value={value}
                              onChange={e => setFieldResponse(field.id, e.target.value)}
                              placeholder={field.placeholder || '+1 (555) 000-0000'}
                              className="w-full px-4 py-3 text-sm border border-slate-200 rounded-xl bg-white outline-none focus:border-brand"
                            />
                          )}

                          {field.type === 'date' && (
                            <input
                              type="date"
                              required={field.required !== false}
                              value={value}
                              onChange={e => setFieldResponse(field.id, e.target.value)}
                              className="w-full px-4 py-3 text-sm border border-slate-200 rounded-xl bg-white outline-none focus:border-brand"
                            />
                          )}

                          {field.type === 'dropdown' && (
                            <select
                              required={field.required !== false}
                              value={value}
                              onChange={e => setFieldResponse(field.id, e.target.value)}
                              className="w-full px-4 py-3 text-sm border border-slate-200 rounded-xl bg-white outline-none focus:border-brand"
                            >
                              <option value="">{field.placeholder || '-- Select an option --'}</option>
                              {opts.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                            </select>
                          )}

                          {field.type === 'multi_select' && (
                            <div className="space-y-1.5">
                              {opts.map(opt => (
                                <label key={opt} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={Array.isArray(value) && value.includes(opt)}
                                    onChange={e => {
                                      const current = Array.isArray(value) ? value : [];
                                      setFieldResponse(field.id, e.target.checked ? [...current, opt] : current.filter((v: string) => v !== opt));
                                    }}
                                    className="w-4 h-4 text-brand rounded border-slate-300"
                                  />
                                  {opt}
                                </label>
                              ))}
                            </div>
                          )}

                          {field.type === 'radio' && (
                            <div className="space-y-1.5">
                              {opts.map(opt => (
                                <label key={opt} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                                  <input
                                    type="radio"
                                    name={`radio_${field.id}`}
                                    checked={value === opt}
                                    onChange={() => setFieldResponse(field.id, opt)}
                                    className="w-4 h-4 text-brand border-slate-300 focus:ring-brand"
                                  />
                                  {opt}
                                </label>
                              ))}
                            </div>
                          )}

                          {(field.type === 'checkbox' || field.type === 'yes_no') && (
                            <div className="space-y-1.5">
                              {(field.type === 'yes_no' ? ['Yes', 'No'] : opts).map(opt => (
                                <label key={opt} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                                  <input
                                    type={field.type === 'yes_no' ? 'radio' : 'checkbox'}
                                    name={field.type === 'yes_no' ? `yesno_${field.id}` : undefined}
                                    checked={field.type === 'yes_no' ? value === opt : (Array.isArray(value) && value.includes(opt))}
                                    onChange={e => {
                                      if (field.type === 'yes_no') {
                                        setFieldResponse(field.id, opt);
                                      } else {
                                        const current = Array.isArray(value) ? value : [];
                                        setFieldResponse(field.id, e.target.checked ? [...current, opt] : current.filter((v: string) => v !== opt));
                                      }
                                    }}
                                    className="w-4 h-4 text-brand rounded border-slate-300"
                                  />
                                  {opt}
                                </label>
                              ))}
                            </div>
                          )}

                          {field.type === 'file_upload' && (
                            fieldFile ? (
                              <div className="flex items-center justify-between p-3 bg-white border border-emerald-200 rounded-xl text-xs">
                                <span className="font-semibold text-emerald-800">{fieldFile.filename}</span>
                                <button type="button" onClick={() => handleRemoveFileByFieldId(field.id)} className="text-red-500">
                                  <Trash2 className="h-3.5 w-3.5" />
                                </button>
                              </div>
                            ) : (
                              <div className="relative border-2 border-dashed border-slate-200 bg-white rounded-xl p-4 text-center cursor-pointer">
                                <input type="file" className="absolute inset-0 cursor-pointer opacity-0 w-full h-full" onChange={(e) => handleFileChange(e, field.label, field.id)} />
                                <Upload className="h-5 w-5 text-slate-400 mx-auto mb-1" />
                                <p className="text-xs font-bold text-slate-600">Upload file for {field.label}</p>
                              </div>
                            )
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {request?.responseType === 'form' && getFormFields().length === 0 && (
                  <div className="space-y-4">
                    {/* Render Text Part */}
                    <div className="space-y-2">
                      <p className="text-xs font-bold text-slate-800 uppercase tracking-wider">Part 1: Text Response</p>
                      {(!request.textResponseType || request.textResponseType === 'short_text') && (
                        <input
                          type="text"
                          required
                          value={responseText}
                          onChange={e => setResponseText(e.target.value)}
                          placeholder="Enter your response here..."
                          className="w-full px-4 py-3 text-sm border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-brand-primary/20 rounded-xl bg-white shadow-inner"
                        />
                      )}
                      {request.textResponseType === 'long_text' && (
                        <textarea
                          required
                          rows={4}
                          value={responseText}
                          onChange={e => setResponseText(e.target.value)}
                          placeholder="Provide your detailed response here..."
                          className="w-full px-4 py-3 text-sm border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-brand-primary/20 rounded-2xl bg-white shadow-inner leading-relaxed"
                        />
                      )}
                      {request.textResponseType === 'date' && (
                        <input
                          type="date"
                          required
                          value={responseText}
                          onChange={e => setResponseText(e.target.value)}
                          className="w-full px-4 py-3 text-sm border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-brand-primary/20 rounded-xl bg-white shadow-inner"
                        />
                      )}
                      {request.textResponseType === 'dropdown' && (
                        <select
                          required
                          value={responseText}
                          onChange={e => setResponseText(e.target.value)}
                          className="w-full px-4 py-3 text-sm border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-brand-primary/20 rounded-xl bg-white shadow-inner font-semibold text-slate-700 cursor-pointer"
                        >
                          <option value="">-- Select an option --</option>
                          {(request?.dropdownOptions || []).map((opt: string, oIdx: number) => (
                            <option key={oIdx} value={opt}>{opt}</option>
                          ))}
                        </select>
                      )}
                    </div>

                    {/* Render File Part */}
                    <div className="space-y-2 border-t border-slate-100 pt-4">
                      <p className="text-xs font-bold text-slate-800 uppercase tracking-wider">Part 2: File Upload</p>
                      {request.requiredDocs && Array.isArray(request.requiredDocs) && request.requiredDocs.length > 0 ? (
                        <div className="space-y-4">
                          {request.requiredDocs.map((docName: string, dIdx: number) => {
                            const fileForDoc = selectedFiles.find(f => f.docName === docName);
                            return (
                              <div key={dIdx} className="space-y-1.5 p-4 bg-slate-50/50 rounded-2xl border border-slate-200">
                                <p className="text-xs font-bold text-slate-700 uppercase tracking-wider">{docName} Upload Box</p>
                                {fileForDoc ? (
                                  <div className="flex items-center justify-between p-3 bg-white border border-emerald-200 rounded-xl text-xs shadow-sm">
                                    <div className="flex items-center space-x-2.5 truncate max-w-[240px]">
                                      <CheckCircle className="h-4 w-4 text-emerald-600 shrink-0" />
                                      <div className="truncate">
                                        <p className="font-semibold text-emerald-800 truncate">{fileForDoc.filename}</p>
                                        <p className="text-[10px] text-slate-400">{formatSize(fileForDoc.size)}</p>
                                      </div>
                                    </div>
                                    <button
                                      type="button"
                                      onClick={() => handleRemoveFileByDocName(docName)}
                                      className="p-1.5 text-slate-400 hover:text-red-500 rounded-lg hover:bg-slate-50 shrink-0 cursor-pointer"
                                    >
                                      <Trash2 className="h-3.5 w-3.5" />
                                    </button>
                                  </div>
                                ) : (
                                  <div className="relative border-2 border-dashed border-slate-200 hover:border-blue-500 bg-white hover:bg-blue-50/10 transition-all rounded-xl p-4 text-center cursor-pointer">
                                    <input
                                      type="file"
                                      accept="application/pdf,image/*,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
                                      className="absolute inset-0 cursor-pointer opacity-0 w-full h-full"
                                      onChange={(e) => handleFileChange(e, docName)}
                                    />
                                    <Upload className="h-5 w-5 text-slate-400 mx-auto mb-1" />
                                    <p className="text-xs font-bold text-slate-600">Upload {docName}</p>
                                    <p className="text-[11px] text-slate-400 mt-0.5">
                                      PDF, JPEG, PNG, GIF, Word, TXT (Max 10MB)
                                    </p>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <div className="relative border-2 border-dashed border-slate-200 hover:border-blue-500 bg-slate-50 hover:bg-blue-50/20 transition-all rounded-2xl p-6 text-center cursor-pointer">
                            <input
                              type="file"
                              multiple
                              accept="application/pdf,image/*,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
                              className="absolute inset-0 cursor-pointer opacity-0 w-full h-full"
                              onChange={handleFileChange}
                            />
                            <Upload className="h-8 w-8 text-slate-400 mx-auto mb-2" />
                            <p className="text-xs font-extrabold text-slate-700">Click or Drag & Drop to Upload Files</p>
                            <p className="text-[10px] text-slate-400 mt-1.5 leading-relaxed">
                              Allowed: PDF, JPEG, PNG, GIF, Word, TXT (Max 10MB per file)
                            </p>
                          </div>

                          {selectedFiles.length > 0 && (
                            <div className="space-y-1.5 pt-1">
                              <p className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider">
                                Uploaded Files ({selectedFiles.length})
                              </p>
                              <div className="space-y-1.5 max-h-[160px] overflow-y-auto">
                                {selectedFiles.map((file, idx) => (
                                  <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 border border-slate-100 rounded-xl text-xs">
                                    <div className="flex items-center space-x-2.5 truncate max-w-[240px]">
                                      <Paperclip className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                                      <div className="truncate">
                                        <p className="font-semibold text-slate-700 truncate">{file.filename}</p>
                                        <p className="text-[10px] text-slate-400">{formatSize(file.size)}</p>
                                      </div>
                                    </div>
                                    <button
                                      type="button"
                                      onClick={() => handleRemoveFile(idx)}
                                      className="p-1.5 text-slate-400 hover:text-red-500 rounded-lg hover:bg-slate-100 shrink-0 cursor-pointer"
                                    >
                                      <Trash2 className="h-3.5 w-3.5" />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {request?.responseType === 'file_upload' && (
                  <div className="space-y-3">
                    {request.requiredDocs && Array.isArray(request.requiredDocs) && request.requiredDocs.length > 0 ? (
                      <div className="space-y-4">
                        {request.requiredDocs.map((docName: string, dIdx: number) => {
                          const fileForDoc = selectedFiles.find(f => f.docName === docName);
                          return (
                            <div key={dIdx} className="space-y-1.5 p-4 bg-slate-50/50 rounded-2xl border border-slate-200">
                              <p className="text-xs font-bold text-slate-700 uppercase tracking-wider">{docName} Upload Box</p>
                              {fileForDoc ? (
                                <div className="flex items-center justify-between p-3 bg-white border border-emerald-200 rounded-xl text-xs shadow-sm">
                                  <div className="flex items-center space-x-2.5 truncate max-w-[240px]">
                                    <CheckCircle className="h-4 w-4 text-emerald-600 shrink-0" />
                                    <div className="truncate">
                                      <p className="font-semibold text-emerald-800 truncate">{fileForDoc.filename}</p>
                                      <p className="text-[10px] text-slate-400">{formatSize(fileForDoc.size)}</p>
                                    </div>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => handleRemoveFileByDocName(docName)}
                                    className="p-1.5 text-slate-400 hover:text-red-500 rounded-lg hover:bg-slate-50 shrink-0 cursor-pointer"
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </button>
                                </div>
                              ) : (
                                <div className="relative border-2 border-dashed border-slate-200 hover:border-blue-500 bg-white hover:bg-blue-50/10 transition-all rounded-xl p-4 text-center cursor-pointer">
                                  <input
                                    type="file"
                                    accept="application/pdf,image/*,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
                                    className="absolute inset-0 cursor-pointer opacity-0 w-full h-full"
                                    onChange={(e) => handleFileChange(e, docName)}
                                  />
                                  <Upload className="h-5 w-5 text-slate-400 mx-auto mb-1" />
                                  <p className="text-xs font-bold text-slate-600">Upload {docName}</p>
                                  <p className="text-[11px] text-slate-400 mt-0.5">
                                    PDF, JPEG, PNG, GIF, Word, TXT (Max 10MB)
                                  </p>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {/* Drag-Drop / Upload Area */}
                        <div className="relative border-2 border-dashed border-slate-200 hover:border-blue-500 bg-slate-50 hover:bg-blue-50/20 transition-all rounded-2xl p-6 text-center cursor-pointer">
                          <input
                            type="file"
                            multiple
                            accept="application/pdf,image/*,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
                            className="absolute inset-0 cursor-pointer opacity-0 w-full h-full"
                            onChange={handleFileChange}
                          />
                          <Upload className="h-8 w-8 text-slate-400 mx-auto mb-2" />
                          <p className="text-xs font-extrabold text-slate-700">Click or Drag & Drop to Upload Files</p>
                          <p className="text-[10px] text-slate-400 mt-1.5 leading-relaxed">
                            Allowed: PDF, JPEG, PNG, GIF, Word, TXT (Max 10MB per file)
                          </p>
                        </div>

                        {/* Selected Files List */}
                        {selectedFiles.length > 0 && (
                          <div className="space-y-1.5 pt-1">
                            <p className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider">
                              Uploaded Files ({selectedFiles.length})
                            </p>
                            <div className="space-y-1.5 max-h-[160px] overflow-y-auto">
                              {selectedFiles.map((file, idx) => (
                                <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 border border-slate-100 rounded-xl text-xs">
                                  <div className="flex items-center space-x-2.5 truncate max-w-[240px]">
                                    <Paperclip className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                                    <div className="truncate">
                                      <p className="font-semibold text-slate-700 truncate">{file.filename}</p>
                                      <p className="text-[10px] text-slate-400">{formatSize(file.size)}</p>
                                    </div>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => handleRemoveFile(idx)}
                                    className="p-1.5 text-slate-400 hover:text-red-500 rounded-lg hover:bg-slate-100 shrink-0 cursor-pointer"
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Submission Controls */}
            <div className="pt-6 border-t border-slate-100 shrink-0">
              <button
                type="submit"
                disabled={submitting}
                className="w-full py-3.5 px-4 text-white font-extrabold text-sm rounded-2xl shadow-lg transition-all flex items-center justify-center space-x-2 cursor-pointer disabled:bg-slate-300 disabled:shadow-none"
                style={{ backgroundColor: accentColor }}
              >
                {submitting ? (
                  <>
                    <Loader className="h-4 w-4 animate-spin text-white" />
                    <span>Submitting response...</span>
                  </>
                ) : (
                  <span>Submit Response</span>
                )}
              </button>
              <p className="text-[10px] text-slate-400 text-center mt-2.5">
                This response will be sent directly to your advisor securely.
              </p>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
}
